import type React from 'react';

import { SocialSharing, SocialSharingGroup } from '@change/ds-core-ui';
import type { Icon } from '@change/ds-icons';
import { EmailOutlineIcon, FacebookRegularIcon, TwitterIcon, WhatsAppIcon } from '@change/ds-icons';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { useI18n } from '@change-corgi/core-react/i18n';

import { useShareLinks } from './hooks/useShareLinks';
import type { Channel } from './hooks/useShareLinks/types';

export const ShareLinks: React.FC<EmptyProps> = () => {
	const {
		data: { shareActions },
	} = useShareLinks();

	return (
		<SocialSharingGroup className="mt-6 mb-0 flex flex-wrap gap-1 md:justify-start">
			{shareActions.map(({ channel, ShareAction }) => (
				<ShareAction key={`share-icon-${channel}`}>
					<ShareIcon channel={channel} />
				</ShareAction>
			))}
		</SocialSharingGroup>
	);
};

const socialIcons: Readonly<
	Record<
		Channel,
		Readonly<{
			icon: React.FC<Icon>;
			displayLabel: string;
		}>
	>
> = {
	facebook: {
		icon: FacebookRegularIcon,
		displayLabel: 'fe.components.psf.share.buttons.short.facebook',
	},
	twitter: {
		icon: TwitterIcon,
		displayLabel: 'fe.components.psf.share.buttons.short.twitter',
	},
	whatsapp: {
		icon: WhatsAppIcon,
		displayLabel: 'fe.components.psf.share.buttons.short.whatsapp',
	},
	email: {
		icon: EmailOutlineIcon,
		displayLabel: 'fe.components.psf.share.buttons.short.email',
	},
} as const;

const ShareIcon: React.FC<{ channel: Channel | undefined; onClick?: () => void }> = ({ channel, onClick }) => {
	const { typedTranslate } = useI18n();
	const { icon: IconComponent, displayLabel } = socialIcons[channel as keyof typeof socialIcons];
	const variant = channel === 'email' ? 'default' : channel;
	return (
		<SocialSharing
			data-testid={`share-icon-${channel}`}
			icon={<IconComponent />}
			label={typedTranslate(displayLabel as unknown as TranslationKey)}
			onClick={onClick}
			size="large"
			variant={variant}
		/>
	);
};
